"use client";

import * as React from "react";

import { useAddToCart } from "~/components/product/actions/use-add-to-cart";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { formatMoney, parseMoney } from "~/lib/money";
import type { GiftCardConfig } from "~/server/api/schemas/brand-settings";
import type { PdpProduct } from "~/server/api/schemas/pdp";
import { useTranslator } from "~/i18n/provider";

/**
 * P6-14 · Gift-card purchase form (PDP). Pick a denomination (a variant) or a
 * custom amount (a `priceMinor = 0` variant, when the brand allows it), fill in
 * the recipient + optional gifting details, and add to cart. The custom amount
 * is display-only here — `cart.addItem` re-clamps it server-side to the brand's
 * min/max; the fixed-denomination price is always the variant's own price.
 *
 * P11-20 · Stored value is base-anchored: a card bought in a switched display
 * currency is ISSUED in the brand's base currency (see `create-order`). So the
 * price on this page and the value the card ends up holding are two different
 * currencies, and the shopper has to be told which before they buy — a €110
 * purchase that arrives as a £50 card is otherwise indistinguishable from a
 * mistake.
 */
export function GiftCardPurchaseForm({
  product,
  config,
  baseCurrency,
}: {
  product: PdpProduct;
  config: GiftCardConfig;
  /**
   * P11-20 · The brand's BASE currency — the one a card is actually issued in.
   * Differs from `product.currency` only when the shopper has switched display
   * currency, which is exactly when the disclosure below has to appear.
   */
  baseCurrency?: string;
}) {
  const t = useTranslator();
  const denominations = React.useMemo(
    () =>
      product.variants
        .filter((v) => v.priceMinor > 0)
        .sort((a, b) => a.priceMinor - b.priceMinor),
    [product.variants],
  );
  const customVariant = React.useMemo(
    () =>
      config.allowCustomAmount
        ? (product.variants.find((v) => v.priceMinor === 0) ?? null)
        : null,
    [product.variants, config.allowCustomAmount],
  );
  const currency = product.currency;

  // Selected: a denomination id, or the sentinel "custom".
  const [choice, setChoice] = React.useState<string>(
    denominations[0]?.id ?? (customVariant ? "custom" : ""),
  );
  const [customAmount, setCustomAmount] = React.useState("");
  const [recipientEmail, setRecipientEmail] = React.useState("");
  const [recipientName, setRecipientName] = React.useState("");
  const [senderName, setSenderName] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [deliveryAt, setDeliveryAt] = React.useState("");

  const isCustom = choice === "custom";
  const selectedVariant = isCustom
    ? customVariant
    : (denominations.find((v) => v.id === choice) ?? null);

  // Server re-clamps; here we validate for UX + a live total.
  let customMinorOrNull: number | null = null;
  if (isCustom && customAmount.trim()) {
    try {
      customMinorOrNull = parseMoney(customAmount, currency);
    } catch {
      customMinorOrNull = null;
    }
  }
  const customInRange =
    customMinorOrNull != null &&
    customMinorOrNull >= config.customMinMinor &&
    customMinorOrNull <= config.customMaxMinor;
  const faceMinor = isCustom
    ? customInRange
      ? customMinorOrNull
      : null
    : (selectedVariant?.priceMinor ?? null);

  const emailValid = /.+@.+\..+/.test(recipientEmail.trim());
  const canAdd = Boolean(selectedVariant) && faceMinor != null && emailValid;

  const giftCard = React.useMemo(
    () => ({
      recipientEmail: recipientEmail.trim(),
      recipientName: recipientName.trim() || undefined,
      senderName: senderName.trim() || undefined,
      message: message.trim() || undefined,
      deliveryAt: deliveryAt
        ? new Date(`${deliveryAt}T09:00:00`).toISOString()
        : undefined,
      amountMinor: isCustom && customInRange ? customMinorOrNull! : undefined,
    }),
    [
      recipientEmail,
      recipientName,
      senderName,
      message,
      deliveryAt,
      isCustom,
      customInRange,
      customMinorOrNull,
    ],
  );

  const { add, isAdding } = useAddToCart({
    variantId: selectedVariant?.id ?? "",
    quantity: 1,
    personalisation: null,
    giftCard,
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    void add("pdp");
  };

  return (
    <form
      onSubmit={onSubmit}
      className="mt-6 space-y-5"
      aria-label={t("pdp.buyGiftCard")}
    >
      {/* Amount */}
      <fieldset>
        <legend className="text-sm font-medium text-fg">Amount</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {denominations.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setChoice(v.id)}
              data-selected={choice === v.id || undefined}
              className={`rounded-full border px-4 py-2 text-sm font-semibold ${
                choice === v.id
                  ? "border-fg text-fg"
                  : "border-border text-fg-muted"
              }`}
            >
              {formatMoney(v.priceMinor, v.currency)}
            </button>
          ))}
          {customVariant ? (
            <button
              type="button"
              onClick={() => setChoice("custom")}
              data-selected={isCustom || undefined}
              className={`rounded-full border px-4 py-2 text-sm font-semibold ${
                isCustom ? "border-fg text-fg" : "border-border text-fg-muted"
              }`}
            >
              Custom
            </button>
          ) : null}
        </div>
        {isCustom ? (
          <div className="mt-3 max-w-[12rem]">
            <Input
              inputMode="decimal"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder={`${formatMoney(config.customMinMinor, currency)}–${formatMoney(
                config.customMaxMinor,
                currency,
              )}`}
              aria-label={t("pdp.customAmount")}
            />
            {customAmount.trim() && !customInRange ? (
              <p className="mt-1 text-xs text-danger">
                Enter an amount between{" "}
                {formatMoney(config.customMinMinor, currency)} and{" "}
                {formatMoney(config.customMaxMinor, currency)}.
              </p>
            ) : null}
          </div>
        ) : null}
      </fieldset>

      {/* Recipient + gifting */}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-fg">
            {t("pdp.recipientEmail")}
          </span>
          <Input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="friend@example.com"
            required
          />
        </label>
        <label>
          <span className="mb-1 block text-sm font-medium text-fg">
            {t("pdp.recipientName")}
          </span>
          <Input
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
          />
        </label>
        <label>
          <span className="mb-1 block text-sm font-medium text-fg">
            {t("pdp.yourName")}
          </span>
          <Input
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
          />
        </label>
        <label className="sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-fg">
            {t("pdp.messageOptional")}
          </span>
          <textarea
            value={message}
            maxLength={500}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          />
        </label>
        <label>
          <span className="mb-1 block text-sm font-medium text-fg">
            {t("pdp.deliveryDateOptional")}
          </span>
          <Input
            type="date"
            value={deliveryAt}
            onChange={(e) => setDeliveryAt(e.target.value)}
          />
        </label>
      </div>

      <div className="flex items-center gap-4">
        <Button type="submit" disabled={!canAdd || isAdding}>
          {isAdding ? "Adding…" : "Add gift card to bag"}
        </Button>
        {faceMinor != null ? (
          <span className="text-sm text-fg-muted">
            Total{" "}
            <span className="font-semibold text-fg">
              {formatMoney(faceMinor, currency)}
            </span>
          </span>
        ) : null}
      </div>
      {/*
        P11-20 · Named, not converted. The exact base figure isn't available
        client-side — the PDP carries converted prices only — and inventing one
        by dividing by a rate we don't hold would risk showing a number the card
        doesn't match. Naming the currency is the part that prevents the
        surprise; the issued email then states the actual amount.
      */}
      {baseCurrency && baseCurrency !== currency ? (
        <p className="text-xs text-fg-muted">
          Gift cards are issued in {baseCurrency}, our store currency.
          You&rsquo;ll pay in {currency}, and the card will hold the equivalent{" "}
          {baseCurrency} value — the amount is confirmed in the email to your
          recipient.
        </p>
      ) : null}
      {deliveryAt ? (
        <p className="text-xs text-fg-muted">
          We&rsquo;ll email the code to your recipient on{" "}
          {new Date(deliveryAt).toLocaleDateString("en-GB")}.
        </p>
      ) : (
        <p className="text-xs text-fg-muted">{t("pdp.giftCardEmailNote")}</p>
      )}
    </form>
  );
}
